import React, { useState, useRef, useEffect } from 'react';
import { CaseFile, CalendarEvent, UserData, LegalDocument } from '../types';
import { db } from '../services/databaseService';
import { RateLimitService } from '../services/rateLimitService';
import { toast } from 'sonner';

interface LiveAssistantProps {
  isOpen: boolean;
  onClose: () => void;
  cases: CaseFile[];
  events: CalendarEvent[];
  documents: LegalDocument[];
  user: UserData;
}

const LiveAssistant: React.FC<LiveAssistantProps> = ({ isOpen, onClose, cases, events, documents, user }) => {
  const [isActive, setIsActive] = useState(false);
  const isActiveRef = useRef(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [status, setStatus] = useState('Pregătit pentru consult vocal');
  const [sessionDuration, setSessionDuration] = useState(0);

  const audioContextInRef = useRef<AudioContext | null>(null);
  const audioContextOutRef = useRef<AudioContext | null>(null);
  const nextStartTimeRef = useRef(0);
  const sourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const streamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const [dummyProcessor, setDummyProcessor] = useState<any>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  // Auto-start session when opened
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (isOpen && !isActive && !isConnecting) {
      const checkAndStart = async () => {
        // Verificăm limita înainte de auto-start
        const isAllowed = await RateLimitService.checkDailyLimit('voiceMinutes');
        if (!isAllowed) {
          toast.error("Limită Timp Vocal Atinsă", { 
            description: `Ai epuizat minutele de voce pentru testarea Beta.` 
          });
          return;
        }

        timeout = setTimeout(() => {
          startSession(true);
        }, 500);
      };
      checkAndStart();
    }
    return () => clearTimeout(timeout);
  }, [isOpen]);

  // Timer logic for session duration and limits
  useEffect(() => {
    if (isActive) {
      timerRef.current = setInterval(() => {
        setSessionDuration(prev => {
          const newDuration = prev + 1;
          
          // La fiecare 60 de secunde, consumăm 1 minut
          if (newDuration % 60 === 0) {
            RateLimitService.incrementUsage('voiceMinutes', 1).then(async () => {
              // Verificăm dacă am depășit limita după incrementare
              const isAllowed = await RateLimitService.checkDailyLimit('voiceMinutes');
              if (!isAllowed) {
                stopSession();
                toast.error("Sesiune Încheiată Automat", { 
                  description: "Limita de timp vocal pentru ziua curentă a fost atinsă." 
                });
              }
            });
          }
          return newDuration;
        });
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      setSessionDuration(0);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isActive]);

  const decode = (base64: string) => {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
    return bytes;
  };

  const encode = (bytes: Uint8Array) => {
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  };

  const decodeAudioData = async (data: Uint8Array, ctx: AudioContext, sampleRate: number, numChannels: number) => {
    const dataInt16 = new Int16Array(data.buffer);
    const frameCount = dataInt16.length / numChannels;
    const buffer = ctx.createBuffer(numChannels, frameCount, sampleRate);
    for (let channel = 0; channel < numChannels; channel++) {
      const channelData = buffer.getChannelData(channel);
      for (let i = 0; i < frameCount; i++) {
        channelData[i] = dataInt16[i * numChannels + channel] / 32768.0;
      }
    }
    return buffer;
  };

  const startSession = async (isAutoStart = false) => {
    if (isConnecting || isActive) return;

    // Check limits again explicitly
    const isAllowed = await RateLimitService.checkDailyLimit('voiceMinutes');
    if (!isAllowed) {
        if (!isAutoStart) {
            toast.error("Limită Timp Vocal Atinsă", { 
                description: "Ai epuizat minutele disponibile azi." 
            });
        }
        return;
    }
    
    setIsConnecting(true);
    setStatus('Inițializare conexiune securizată...');

    try {
      // 1. Setup Audio Contexts
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      audioContextInRef.current = new AudioContextClass({ sampleRate: 16000 });
      audioContextOutRef.current = new AudioContextClass({ sampleRate: 24000 });

      if (audioContextInRef.current?.state === 'suspended') {
        try { await audioContextInRef.current.resume(); } catch (e) { console.warn("Auto-resume failed", e); }
      }
      if (audioContextOutRef.current?.state === 'suspended') {
        try { await audioContextOutRef.current.resume(); } catch (e) { console.warn("Auto-resume failed", e); }
      }
      
      setStatus('Accesare microfon...');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      streamRef.current = stream;

      setStatus('Conectare la LexSmart Neural Core...');
      const apiKey = (import.meta as any).env.VITE_GOOGLE_AI_KEY;
      if (!apiKey) throw new Error("Cheia API lipsă sau neconfigurată.");

      setStatus('Se descarcă personalitatea vocală (Prompt)...');
      let customSystemInstruction = '';
      try {
        const customPrompt = await db.getPromptByName('Consult Vocal (LexSmart Voice)');
        if (customPrompt && customPrompt.system_instruction) {
          customSystemInstruction = customPrompt.system_instruction;
        }
      } catch (err) {
        console.warn('Nu s-a putut citi prompt-ul din DB, se folosește cel hardcodat de siguranță.', err);
      }

      // Build detailed context for the AI
      const todayEventsText = events
        .filter(e => new Date(e.date).toDateString() === new Date().toDateString())
        .map(e => `- ${new Date(e.date).toLocaleTimeString('ro-RO', {hour: '2-digit', minute:'2-digit'})}: ${e.title} (${e.type})`)
        .join('\n') || "Niciun eveniment azi.";

      const recentCasesText = cases
        .slice(0, 5)
        .map(c => `- ${c.caseNumber}: ${c.clientName} (Status: ${c.status})`)
        .join('\n');

      const recentDocsText = documents
        .slice(0, 5)
        .map(d => `- ${(d as any).name || (d as any).title || 'Document'} (${d.type}) - ${d.summary ? d.summary.substring(0, 50) + '...' : 'Fără rezumat'}`)
        .join('\n') || "Niciun document recent.";

      const promptText = customSystemInstruction
        ? customSystemInstruction
            .replace('{USER_NAME}', user.name)
            .replace('{EVENTS}', todayEventsText)
            .replace('{CASES}', recentCasesText)
            .replace('{DOCS}', recentDocsText)
            .replace('{CASES_COUNT}', cases.length.toString())
            .replace('{EVENTS_COUNT}', events.length.toString())
            .replace('{DOCS_COUNT}', documents.length.toString())
        : 'Ești LexSmart Voice, asistentul vocal al unui avocat de elită. Răspunde scurt și la obiect în limba română.';

      const host = "generativelanguage.googleapis.com";
      const wsUrl = `wss://${host}/ws/google.ai.generativelanguage.v1alpha.GenerativeService.BidiGenerateContent?key=${apiKey}`;
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = async () => {
        console.log("WebSocket conectat");
        ws.send(JSON.stringify({
          setup: {
            model: "models/gemini-2.0-flash-exp",
            systemInstruction: { parts: [{text: promptText}] },
            generationConfig: {
              responseModalities: ["AUDIO"],
              speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: "Zephyr" } } }
            }
          }
        }));

        setStatus('Ascult... Întreabă-mă orice.');
        setIsActive(true);
        isActiveRef.current = true;
        setIsConnecting(false);
        setTranscription('');
        if (!isAutoStart) toast.success("Sesiune Vocală Activă");

        if (audioContextInRef.current?.state === 'suspended') await audioContextInRef.current.resume();

        if (audioContextInRef.current?.state === 'running') {
          const source = audioContextInRef.current.createMediaStreamSource(stream);
          processorRef.current = audioContextInRef.current.createScriptProcessor(4096, 1, 1);

          processorRef.current.onaudioprocess = (e) => {
            if (!isActiveRef.current || ws.readyState !== WebSocket.OPEN) return;

            const inputData = e.inputBuffer.getChannelData(0);
            const int16 = new Int16Array(inputData.length);
            for (let i = 0; i < inputData.length; i++) int16[i] = inputData[i] * 32768;

            const base64Audio = encode(new Uint8Array(int16.buffer));
            ws.send(JSON.stringify({
              realtimeInput: {
                mediaChunks: [{ mimeType: "audio/pcm;rate=16000", data: base64Audio }]
              }
            }));
          };

          source.connect(processorRef.current);
          processorRef.current.connect(audioContextInRef.current.destination);
          setDummyProcessor(processorRef.current);
        }
      };

      ws.onmessage = async (event) => {
        try {
            let data;
            if (event.data instanceof Blob) {
                const text = await event.data.text();
                data = JSON.parse(text);
            } else {
                data = JSON.parse(event.data);
            }

            if (data.serverContent?.modelTurn?.parts) {
                for (const part of data.serverContent.modelTurn.parts) {
                    if (part.inlineData && part.inlineData.data) {
                        const audioData = part.inlineData.data;
                        if (audioContextOutRef.current) {
                            const ctx = audioContextOutRef.current;
                            if (ctx.state === 'suspended') await ctx.resume();

                            nextStartTimeRef.current = Math.max(nextStartTimeRef.current, ctx.currentTime);
                            const buffer = await decodeAudioData(decode(audioData), ctx, 24000, 1);
                            const source = ctx.createBufferSource();
                            source.buffer = buffer;
                            source.connect(ctx.destination);
                            source.start(nextStartTimeRef.current);
                            nextStartTimeRef.current += buffer.duration;
                            sourcesRef.current.add(source);
                            source.onended = () => sourcesRef.current.delete(source);
                        }
                    }
                    if (part.text) {
                        setTranscription(prev => prev + ' ' + part.text);
                    }
                }
            }
            if (data.serverContent?.interrupted) {
                sourcesRef.current.forEach(s => s.stop());
                sourcesRef.current.clear();
                nextStartTimeRef.current = 0;
            }
        } catch(e) {
            console.error("Eroare la parsarea mesajului ws:", e);
        }
      };

      ws.onclose = (event) => {
        console.log("WebSocket închis", event.code, event.reason);
        if (isActiveRef.current) {
            if (!isAutoStart && event.code !== 1000 && event.code !== 1005) toast.error("Sesiune Întreruptă", { description: "Conexiunea cu serverul a fost pierdută." });
            stopSession();
        }
      };

      ws.onerror = (error) => {
        console.error("Eroare WebSocket:", error);
        if (!isAutoStart && isActiveRef.current) {
            toast.error("Eroare Sesiune", { description: "Eroare la nivel de rețea." });
        }
        stopSession();
      };

    } catch (err: any) {
      console.error("Eroare la pornire sesiune:", err);
      // Don't toast if auto-start failed (might be expected due to autoplay policies)
      if (!isAutoStart) toast.error("Eroare conexiune", { description: err.message || "Nu s-a putut iniția sesiunea vocală." });
      stopSession();
    }
  };

  const stopSession = () => {
    setIsActive(false);
    isActiveRef.current = false;
    setIsConnecting(false);
    setStatus('Sesiune încheiată');
    
    try {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ clientContent: { turnComplete: true } }));
          wsRef.current.close(1000, "User requested close");
      }
      wsRef.current = null;
      streamRef.current?.getTracks().forEach(t => t.stop());
      processorRef.current?.disconnect();
      audioContextInRef.current?.close();
      audioContextOutRef.current?.close();
      sourcesRef.current.forEach(s => s.stop());
      sourcesRef.current.clear();
    } catch(e) { console.error(e); }
    
    nextStartTimeRef.current = 0;
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[300] bg-slate-900/90 backdrop-blur-2xl flex items-center justify-center p-6 animate-in fade-in duration-300">
      <div className="w-full max-w-2xl bg-[#020617] border border-white/10 rounded-[3rem] p-10 md:p-16 text-center space-y-10 relative overflow-hidden shadow-2xl">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,_#4f46e5_0%,_transparent_70%)]"></div>
        </div>

        <button onClick={() => { stopSession(); onClose(); }} className="absolute top-8 right-8 text-white/20 hover:text-white text-4xl font-light transition-colors">×</button>

        <div className="relative z-10 flex flex-col items-center gap-12">
          <div className="relative">
            <div className={`w-32 h-32 md:w-48 md:h-48 rounded-full bg-indigo-600 flex items-center justify-center text-5xl md:text-7xl shadow-[0_0_80px_rgba(79,70,229,0.4)] transition-all duration-500 ${isActive ? 'scale-110 animate-pulse ring-8 ring-emerald-500/20' : 'grayscale opacity-50'} ${isConnecting ? 'animate-bounce' : ''}`}>
               {isConnecting ? '⏳' : '🎙️'}
            </div>
            {isActive && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-full h-full rounded-full border-4 border-indigo-500/30 animate-ping"></div>
              </div>
            )}
            {isActive && (
                <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 font-mono text-emerald-400 text-xl font-bold tracking-widest bg-black/50 px-3 py-1 rounded-lg">
                    {formatTime(sessionDuration)}
                </div>
            )}
          </div>

          <div className="space-y-4">
            <h2 className="text-3xl md:text-5xl font-black italic text-white tracking-tighter uppercase">{isActive ? 'Consult Live' : 'LexSmart Voice'}</h2>
            <p className="text-indigo-400 font-black text-[10px] md:text-xs uppercase tracking-[0.4em] italic">{status}</p>
          </div>

          <div className="w-full max-h-32 overflow-y-auto custom-scrollbar min-h-[60px]">
            <p className="text-white/60 text-sm md:text-lg italic font-medium leading-relaxed">
              {transcription || (isActive ? "Te ascult..." : "Apasă butonul pentru a începe.")}
            </p>
          </div>

          <button 
            onClick={isActive ? stopSession : () => startSession(false)}
            disabled={isConnecting}
            className={`px-12 py-6 rounded-2xl font-black text-xs uppercase tracking-[0.3em] transition-all active:scale-95 shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed ${isActive ? 'bg-rose-600 text-white shadow-rose-500/20' : 'bg-emerald-600 text-white shadow-emerald-500/20'}`}
          >
            {isConnecting ? 'SE CONECTEAZĂ...' : (isActive ? 'ÎNCHEIE SESIUNEA' : 'PORNEȘTE CONSULT')}
          </button>
        </div>

        <div className="pt-8 border-t border-white/5 relative z-10 flex justify-center items-center gap-3">
          <span className={`w-2 h-2 rounded-full ${isActive ? 'bg-emerald-500 animate-pulse' : 'bg-slate-700'}`}></span>
          <p className="text-[9px] font-black text-white/20 uppercase tracking-widest italic">Criptare End-to-End • LexSmart Voice Protocol</p>
        </div>
      </div>
    </div>
  );
};

export default LiveAssistant;
